import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useOpeningContext } from '@/contexts/opening-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';

export function HiringOpeningsPopover() {
  const openingSlug = useOpeningSlug();
  const { showAggregateData, setShowCheckbox, currentHiringGoalId } =
    useOpeningContext();
  const [open, setOpen] = useState(false);

  const { data } = useSuspenseQuery({
    // Added showAggregateData to the query key to trigger refetches so hiring openings update with everything else.
    queryKey: [
      'hiring-goals',
      openingSlug,
      currentHiringGoalId,
      showAggregateData,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsHiringOpenings(
        openingSlug,
        currentHiringGoalId,
      ),
  });

  const hiringOpenings = useMemo(
    () => data.hiring_openings?.filter(o => o.slug !== openingSlug) ?? [],
    [data, openingSlug],
  );

  useEffect(() => {
    if (hiringOpenings.length > 0) {
      setShowCheckbox(true);
    }
  }, [setShowCheckbox, hiringOpenings, openingSlug]);

  if (hiringOpenings.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="link" className="h-fit hover:no-underline">
          <FormattedMessage
            defaultMessage="{count} Hiring Openings"
            values={{ count: hiringOpenings.length }}
          />
          {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-w-100 flex w-fit flex-col items-start justify-center"
      >
        {hiringOpenings.map(opening => (
          <div
            key={opening.external_id}
            className="flex w-full items-center justify-between py-2"
          >
            <Typography variant="subtitle1">{opening.title}</Typography>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

HiringOpeningsPopover.Skeleton = function HiringOpeningsPopoverSkeleton() {
  return <Skeleton className="h-5 w-[134px]" />;
};
